// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import etask_lib from '/util/etask.js';
import {useHistory} from 'react-router-dom';
import {useState, useCallback, useEffect, useRef} from 'react';
import {use_setdb} from '/www/util/pub/react_hooks.js';
import {notify_error, notify_success, call_api,
    build_stats_params, scroll_to_id, ls_set, get_partner_link,
    ls_get, get_embedded_data} from './util.js';
import * as api from './api.js';
import {PERMISSION_LVL, PARTNER_PARAMS_META} from './shared/constants.js';
import {has_perm, get_perms, serialize_key} from './shared/util.js';
import {use_request} from './hooks/api.js';
import {use_breadcrumbs} from './hooks/breadcrumbs.js';

export {use_request, use_setdb};

export function use_form({data=null, on_complete, validate, submit}={}){
    const [wrap, etask, {processing}] = use_request();
    const [validity, set_validity] = useState({});
    const [form_data, set_form_data] = useState(data);
    const [changed, set_changed] = useState(false);
    const on_change = useCallback(f_data=>{
        set_validity({});
        set_form_data(d=>({...d, ...f_data}));
    }, []);
    useEffect(()=>{
        if (data?._id!==form_data?._id)
            set_form_data(data);
    }, [form_data, data]);
    const on_submit = useCallback(()=>etask(function*(){
        let valid = true;
        let details;
        if (validate)
            ({valid, details} = validate(form_data));
        if (!valid)
            return set_validity(details);
        try {
            const res = yield wrap(submit(form_data));
            if (res.error)
            {
                notify_error(res.error);
                return false;
            }
            on_complete?.(res);
            set_changed(false);
            return true;
        } catch(e){
            console.log(e);
        }
    }), [form_data, on_complete]);
    return {
        changed,
        validity,
        on_submit,
        set_form_data: on_change,
        set_changed,
        disabled: processing,
    };
}

export function use_onboarding(def_res=true){
    const user = use_user();
    const partner = use_partner();
    const skip_onboarding = !!user?.skip_onboarding;
    if (skip_onboarding)
        return false;
    const is_root = !!user?.is_root;
    if (is_root)
        return false;
    return partner
        ? partner._id=='none'
        : def_res;
}

export function use_can_be_root(){
    const user = use_user();
    return !!user?.can_be_root;
}

export function use_root(){
    const is_root = use_setdb('login.user.is_root');
    return is_root ?? false;
}

export function use_super_root(){
    const user_meta = get_embedded_data('user');
    return !!user_meta.is_super_root;
}

export function use_tier(){
    const partner = use_partner();
    return partner?.tier;
}

export function use_partner(){
    return use_setdb('head.partner.current');
}

export function use_user(){
    return use_setdb('login.user');
}

export function use_perm_data(){
    const user = use_user();
    const user_perm = user ? get_perms(user) : {};
    const embedded_perm = get_embedded_data('user')?.permissions || {};
    const permissions = Object.keys(embedded_perm).length
        ? embedded_perm : user_perm;
    return permissions;
}

export function use_perm(perm, lvl=PERMISSION_LVL.R){
    const is_root = use_root();
    const is_super_root = use_super_root();
    const permissions = use_perm_data();
    return has_perm({is_root, is_super_root, permissions}, perm, lvl);
}

export function use_charts_data(params={}){
    const {app_uuid, partner_uuid, from, to, skip, on_load} = params;
    const [data, set_data] = useState([]);
    const [wrap, etask, {processing}] = use_request();
    useEffect(()=>{
        if (skip || !app_uuid && !partner_uuid)
            return;
        etask(function*(){
            try {
                const opts = build_stats_params({
                    app_uuid,
                    with_money: true,
                    partner_uuid,
                    from,
                    to,
                });
                const res = yield wrap(api.get_basic_chart_cached(opts));
                if (res.error)
                    throw new Error(res.error);
                on_load?.(res);
                set_data(res);
            } catch(e){
                notify_error(e.message);
            }
        });
    }, [app_uuid, partner_uuid, from, to, on_load]);
    return {data, processing};
}

const get_param = name=>{
    const url = new URL(document.location);
    return url.searchParams.get(name);
};

const url2string = url=>`${url.pathname}${url.search}${url.hash}`;

const apply_param = (history, name, val)=>{
    const url = new URL(document.location);
    const initial_href = url2string(url);
    url.searchParams.set(name, val);
    const new_href = url2string(url);
    if (initial_href!=new_href)
        history.push(url2string(url));
};

export function use_search_param(name, default_value, opts={}){
    const {skip} = opts;
    const history = useHistory();
    const [val, set_val] = useState(()=>get_param(name)||default_value);
    useEffect(()=>history.listen(()=>{
        if (skip)
            return;
        set_val(get_param(name));
    }), [name, skip]);
    useEffect(()=>{
        if (skip)
            return;
        apply_param(history, name, val);
    }, [history, name, val, skip]);
    return [val, set_val];
}

export function use_partner_full(uuid, params={}){
    const {skip_crumbs=false, crumb_title} = params;
    const [wrap, etask, {processing}] = use_request();
    const [partner, set_partner] = useState(null);
    useEffect(()=>{
        if (!uuid || uuid=='none' || uuid=='all')
            return void set_partner(null);
        const req_op = etask(function*(){
            set_partner(null);
            const res = yield wrap(call_api(()=>api.get_partner(uuid)));
            if (!res)
                return;
            set_partner(res);
        });
        return ()=>req_op.return();
    }, [uuid]);
    const edit = useCallback(_data=>etask(function*(){
        const data = {};
        for (const [key, _value] of Object.entries(_data))
        {
            const param_meta = PARTNER_PARAMS_META[key];
            let value = _value;
            if (param_meta?.true_bool)
                value = Boolean(value);
            data[serialize_key(key)] = value;
        }
        const res = yield wrap(call_api(()=>api.edit_partner(partner.uuid,
            data)));
        if (!res)
            return;
        set_partner(res);
        notify_success('Changes saved');
        return res;
    }), [partner]);
    use_breadcrumbs({
        title: crumb_title || partner?.company_name || partner?._id || '...',
        path: get_partner_link(uuid),
    }, skip_crumbs);
    return {
        partner,
        processing,
        apply_change: set_partner,
        edit,
    };
}

export function use_checklist(os, version_hash){
    const [wrap, etask, {processing}] = use_request();
    const [checklist, set_checklist] = useState(null);
    useEffect(()=>{
        if (!version_hash || !os)
            return;
        etask(function*(){
            const res = yield wrap(api.get_checklist(os, version_hash));
            if (res.error)
                return notify_error(res.error);
            set_checklist(res);
        });
    }, [version_hash, os]);
    return {checklist, processing};
}

export function use_review(uuid, full_history){
    const [wrap, etask, {processing}] = use_request();
    const [review, set_review] = useState(null);
    useEffect(()=>{
        if (!uuid)
            return;
        etask(function*(){
            const res = yield wrap(call_api(()=>
                api.get_review({uuid, full_history})));
            set_review(res);
        });
    }, [uuid, full_history]);
    const apply_change = useCallback(updated=>set_review(updated), []);
    return {review, processing, apply_change};
}

const close_handler = event=>{
    event.preventDefault();
    return event.returnValue = '';
};

export function use_close_lock(condition=true){
    useEffect(()=>{
        if (condition)
        {
            window.addEventListener('beforeunload', close_handler);
            return ()=>
                window.removeEventListener('beforeunload', close_handler);
        }
    }, [condition]);
    useEffect(()=>{
        return ()=>window.removeEventListener('beforeunload', close_handler);
    }, []);
}

export function use_id_scroll(rec_id){
    const scrolled_ref = useRef({scrolled: false});
    useEffect(()=>{
        if (rec_id && !scrolled_ref.current?.scrolled)
        {
            scrolled_ref.current.scrolled = true;
            scroll_to_id();
        }
    }, [rec_id]);
}

export function use_render_on_url_change(){
    const [, set_counter] = useState(0);
    const history = useHistory();
    // to re-render component when location changed (i.e. hash)
    useEffect(()=>history.listen(()=>set_counter(c=>c+1)), []);
}

export function use_persisted_value(key, default_value){
    const [val, set_val] = useState(()=>ls_get(key, default_value));
    const change_val = useCallback(new_val=>{
        ls_set(key, new_val);
        set_val(ls_get(key, default_value));
    }, [key, default_value]);
    return [val, change_val];
}

export function use_intervally(cb, opts={}, deps=[]){
    const {interval_ms=5000, skip_first=false} = opts;
    const [c, set_c] = useState(0);
    useEffect(()=>{
        let timer;
        etask_lib(function*(){
            this.finally(()=>
                timer = setTimeout(()=>set_c(v=>v+1), interval_ms));
            if (skip_first && c==0)
                return;
            yield cb();
        });
        return ()=>clearTimeout(timer);
    }, [c, interval_ms, skip_first, ...deps]);
}

export function use_counter(){
    const [c, set_c] = useState(0);
    const inc = useCallback(()=>set_c(v=>v+1), []);
    return {c, inc};
}
